import { useQuery, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  GET_TAX_BASED_AMOUNT_CONFIG,
  GET_TAX_AMOUNT_STATUS,
  GET_TAX_AMOUNT_HISTORY,
  GET_TAX_AMOUNT_STATISTICS,
  UPDATE_TAX_BASED_AMOUNT_CONFIG,
  UPDATE_TAX_CURRENT_AMOUNTS,
} from '../graphql/taxBasedAmountOperations';

// ==================== TYPES ====================

export interface TaxBasedAmountConfig {
  id: string;
  taxedTargetAmount: number;
  taxedAlertAmount: number;
  exemptTargetAmount: number;
  exemptAlertAmount: number;
  lastTaxedAmount: number;
  lastExemptAmount: number;
  lastCheckDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface TaxAmountStatus {
  taxedAmount: number;
  taxedTarget: number;
  taxedAlert: number;
  taxedPercentage: number;
  taxedAlertTriggered: boolean;
  taxedReached: boolean;
  exemptAmount: number;
  exemptTarget: number;
  exemptAlert: number;
  exemptPercentage: number;
  exemptAlertTriggered: boolean;
  exemptReached: boolean;
  date: Date;
}

export interface TaxBasedAmountHistory {
  id: string;
  date: Date;
  taxedAmount: number;
  exemptAmount: number;
  taxedTarget: number;
  exemptTarget: number;
  taxedReached: boolean;
  exemptReached: boolean;
  createdAt: Date;
}

export interface TaxAmountStatistics {
  totalDays: number;
  taxedAverage: number;
  exemptAverage: number;
  taxedReachedCount: number;
  exemptReachedCount: number;
  bothReachedCount: number;
}

export interface UpdateTaxConfigInput {
  taxedTargetAmount?: number;
  taxedAlertAmount?: number;
  exemptTargetAmount?: number;
  exemptAlertAmount?: number;
}

// ==================== HOOKS ====================

/**
 * Hook para obtener la configuración de montos por impuestos
 */
export const useTaxBasedAmountConfig = () => {
  const { data, loading, error, refetch } = useQuery<{
    getTaxBasedAmountConfig: TaxBasedAmountConfig;
  }>(GET_TAX_BASED_AMOUNT_CONFIG, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    config: data?.getTaxBasedAmountConfig,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para obtener el estado actual de ventas por tipo de impuesto
 * @param date - Fecha a consultar (por defecto hoy)
 * @param pollInterval - Intervalo de polling en ms (default: 10000 = 10 segundos)
 */
export const useTaxAmountStatus = (date?: Date, pollInterval: number = 10000) => {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<{
    getTaxAmountStatus: TaxAmountStatus;
  }>(GET_TAX_AMOUNT_STATUS, {
    variables: { date },
    fetchPolicy: 'network-only',
    pollInterval: pollInterval,
  });

  return {
    status: data?.getTaxAmountStatus,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
};

/**
 * Hook para obtener el historial de montos por impuestos
 */
export const useTaxAmountHistory = (
  startDate?: Date,
  endDate?: Date,
  limit: number = 30
) => {
  const { data, loading, error, refetch } = useQuery<{
    getTaxAmountHistory: TaxBasedAmountHistory[];
  }>(GET_TAX_AMOUNT_HISTORY, {
    variables: { startDate, endDate, limit },
    fetchPolicy: 'cache-and-network',
  });

  return {
    history: data?.getTaxAmountHistory || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para obtener estadísticas de montos por impuestos
 */
export const useTaxAmountStatistics = (startDate?: Date, endDate?: Date) => {
  const { data, loading, error, refetch } = useQuery<{
    getTaxAmountStatistics: TaxAmountStatistics;
  }>(GET_TAX_AMOUNT_STATISTICS, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
  });

  return {
    statistics: data?.getTaxAmountStatistics,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para actualizar la configuración de montos
 */
export const useUpdateTaxConfig = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_TAX_BASED_AMOUNT_CONFIG, {
    refetchQueries: [
      { query: GET_TAX_BASED_AMOUNT_CONFIG },
      { query: GET_TAX_AMOUNT_STATUS },
    ],
  });

  const updateConfig = useCallback(
    async (input: UpdateTaxConfigInput) => {
      const result = await updateMutation({ variables: { input } });
      return result.data?.updateTaxBasedAmountConfig;
    },
    [updateMutation]
  );

  return {
    updateConfig,
    updating: loading,
  };
};

/**
 * Hook para actualizar manualmente los montos del día actual
 */
export const useUpdateTaxCurrentAmounts = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_TAX_CURRENT_AMOUNTS, {
    refetchQueries: [
      { query: GET_TAX_AMOUNT_STATUS },
      { query: GET_TAX_AMOUNT_HISTORY },
      { query: GET_TAX_AMOUNT_STATISTICS },
    ],
  });

  const updateCurrentAmounts = useCallback(
    async (taxedAmount: number, exemptAmount: number, date?: Date) => {
      const result = await updateMutation({
        variables: { taxedAmount, exemptAmount, date },
      });
      return result.data?.updateTaxCurrentAmounts;
    },
    [updateMutation]
  );

  return {
    updateCurrentAmounts,
    updating: loading,
  };
};
